import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyPromotion = {
  name: '',
  description: '',
  promo_code: '',
  start_date: '',
  end_date: '',
  discount_type: 'percentage',
  discount_percentage: '',
  flat_discount: '',
  max_discount: '',
  min_cart_value: '',
  free_item_product_id: '',
  buy_x_get_y: { buy_product_id: '', buy_quantity: 1, get_product_id: '', free_quantity: 1 },
  product_targeting: { apply_to: 'all_products', product_id: '', category_id: '' },
  user_targeting: { target_type: 'all_users', emails: '' },
  usage_rules: { total_usage_limit: '', per_user_limit: '' },
  allow_stacking: true,
  image_url: '',
  image_data: '',
};

const emptyGiftCard = { name: '', code: '', type: 'FLAT', value: '', expiry_date: '', usage_limit: 1 };


const toIsoOrNull = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const sanitizeNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};


export default function AdminPromotionsPanel() {
  const [promotions, setPromotions] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [usageLogs, setUsageLogs] = useState([]);
  const [analytics, setAnalytics] = useState({ total_usage: 0, total_discount_given: 0, revenue_generated: 0, top_promotions: [] });
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPromotion);
  const [giftForm, setGiftForm] = useState(emptyGiftCard);

  const hydrate = async () => {
    const [p, g, u, a, m, c] = await Promise.all([
      axios.get(`${API}/promotions`),
      axios.get(`${API}/gift-cards`),
      axios.get(`${API}/promotion-usage-logs`),
      axios.get(`${API}/promotions/analytics`),
      axios.get(`${API}/menu-items`),
      axios.get(`${API}/categories`),
    ]);
    setPromotions(p.data);
    setGiftCards(g.data);
    setUsageLogs(u.data);
    setAnalytics(a.data);
    setMenuItems(m.data);
    setCategories(c.data);
  };

  useEffect(() => {
    hydrate().catch(() => toast.error('Failed to load promotions data'));
  }, []);

  const mapPayload = () => ({
    name: form.name,
    description: form.description || null,
    promo_code: form.promo_code || null,
    start_date: toIsoOrNull(form.start_date),
    end_date: toIsoOrNull(form.end_date),
    discount_type: form.discount_type,
    discount_percentage: sanitizeNumber(form.discount_percentage),
    flat_discount: sanitizeNumber(form.flat_discount),
    max_discount: sanitizeNumber(form.max_discount),
    min_cart_value: sanitizeNumber(form.min_cart_value),
    free_item_product_id: form.free_item_product_id || null,
    buy_x_get_y: form.discount_type === 'buy_x_get_y' ? {
      buy_product_id: form.buy_x_get_y.buy_product_id,
      buy_quantity: Number(form.buy_x_get_y.buy_quantity || 1),
      get_product_id: form.buy_x_get_y.get_product_id,
      free_quantity: Number(form.buy_x_get_y.free_quantity || 1),
    } : null,
    product_targeting: {
      apply_to: form.product_targeting.apply_to,
      product_id: form.product_targeting.product_id || null,
      category_id: form.product_targeting.category_id || null,
    },
    user_targeting: {
      target_type: form.user_targeting.target_type,
      emails: form.user_targeting.target_type === 'specific_users'
        ? form.user_targeting.emails.split(',').map((e) => e.trim()).filter(Boolean)
        : [],
    },
    usage_rules: {
      total_usage_limit: sanitizeNumber(form.usage_rules.total_usage_limit),
      per_user_limit: sanitizeNumber(form.usage_rules.per_user_limit),
    },
    allow_stacking: form.allow_stacking,
    image_url: form.image_url?.trim() ? form.image_url.trim() : null,
    image_data: form.image_data || null,
  });

  const createOrUpdatePromotion = async (e) => {
    e.preventDefault();
    const payload = mapPayload();
    if (!payload.start_date || !payload.end_date) {
      toast.error('Please provide valid start and end date/time');
      return;
    }
    if (new Date(payload.start_date) >= new Date(payload.end_date)) {
      toast.error('End date must be after start date');
      return;
    }

    if ((payload.discount_type === 'percentage' || payload.discount_type === 'first_order') && (payload.discount_percentage === null || payload.discount_percentage <= 0 || payload.discount_percentage > 100)) {
      toast.error('Discount percentage must be between 0 and 100');
      return;
    }

    if (payload.discount_type === 'flat' && (payload.flat_discount === null || payload.flat_discount <= 0)) {
      toast.error('Flat discount must be greater than 0');
      return;
    }

    try {
      if (editing) {
        await axios.put(`${API}/promotions/${editing.id}`, payload);
        toast.success('Promotion updated');
      } else {
        await axios.post(`${API}/promotions`, payload);
        toast.success('Promotion created');
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyPromotion);
      hydrate();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save promotion');
    }
  };

  const editPromotion = (promotion) => {
    setEditing(promotion);
    setForm({
      ...emptyPromotion,
      ...promotion,
      start_date: promotion.start_date?.slice(0, 16) || '',
      end_date: promotion.end_date?.slice(0, 16) || '',
      discount_percentage: promotion.discount_percentage ?? '',
      flat_discount: promotion.flat_discount ?? '',
      max_discount: promotion.max_discount ?? '',
      min_cart_value: promotion.min_cart_value ?? '',
      usage_rules: {
        total_usage_limit: promotion.usage_rules?.total_usage_limit ?? '',
        per_user_limit: promotion.usage_rules?.per_user_limit ?? '',
      },
      user_targeting: {
        target_type: promotion.user_targeting?.target_type || 'all_users',
        emails: (promotion.user_targeting?.emails || []).join(', '),
      },
      buy_x_get_y: promotion.buy_x_get_y || emptyPromotion.buy_x_get_y,
      product_targeting: promotion.product_targeting || emptyPromotion.product_targeting,
      image_url: promotion.image_url || '',
      image_data: promotion.image_data || '',
    });
    setOpen(true);
  };

  const disablePromotion = async (id) => {
    await axios.post(`${API}/promotions/${id}/disable`);
    toast.success('Promotion disabled');
    hydrate();
  };

  const deletePromotion = async (id) => {
    await axios.delete(`${API}/promotions/${id}`);
    toast.success('Promotion deleted');
    hydrate();
  };

  const createGiftCard = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/gift-cards`, {
        ...giftForm,
        value: Number(giftForm.value),
        usage_limit: Number(giftForm.usage_limit),
        expiry_date: new Date(giftForm.expiry_date).toISOString(),
      });
      toast.success('Gift card created');
      setGiftForm(emptyGiftCard);
      hydrate();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create gift card');
    }
  };

  const formatDiscount = (p) => {
    if (p.discount_type === 'percentage' || p.discount_type === 'first_order') return `${p.discount_percentage || 0}%`;
    if (p.discount_type === 'flat') return `₹${p.flat_discount || 0}`;
    if (p.discount_type === 'buy_x_get_y') return 'Buy X Get Y';
    return 'Free item';
  };

  const tableRows = useMemo(() => promotions.map((p) => {
    const usage = usageLogs.filter((u) => u.promotion_id === p.id).length;
    return { ...p, usage };
  }), [promotions, usageLogs]);

  const onOfferImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be 2MB or smaller');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageData = typeof reader.result === 'string' ? reader.result : '';
      setForm((prev) => ({ ...prev, image_data: imageData, image_url: '' }));
    };
    reader.onerror = () => toast.error('Failed to read image file');
    reader.readAsDataURL(file);
  };

  return (
    <Tabs defaultValue="promotions" className="space-y-4">
      <TabsList className="grid grid-cols-4 w-full max-w-2xl">
        <TabsTrigger value="promotions">Promotions</TabsTrigger>
        <TabsTrigger value="giftcards">Gift Cards</TabsTrigger>
        <TabsTrigger value="usage">Usage Logs</TabsTrigger>
        <TabsTrigger value="analytics">Analytics</TabsTrigger>
      </TabsList>

      <TabsContent value="promotions" className="space-y-4">
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setForm(emptyPromotion); }}>+ Create Promotion</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? 'Edit Promotion' : 'Create Promotion'}</DialogTitle></DialogHeader>
              <form onSubmit={createOrUpdatePromotion} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>Promotion Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Promo Code (optional)</Label><Input value={form.promo_code || ''} onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })} /></div>
                  <div className="md:col-span-2"><Label>Description</Label><Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <div className="md:col-span-2">
                    <Label>Offer Photo URL (optional)</Label>
                    <Input
                      placeholder="https://..."
                      value={form.image_url || ''}
                      onChange={(e) => setForm({ ...form, image_url: e.target.value, image_data: '' })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Upload Offer Photo (optional, max 2MB)</Label>
                    <Input type="file" accept="image/*" onChange={onOfferImageUpload} />
                    {(form.image_data || form.image_url) && (
                      <img
                        src={form.image_data || form.image_url}
                        alt="Offer preview"
                        className="mt-2 h-32 w-full max-w-sm object-cover rounded-md border"
                      />
                    )}
                  </div>
                  <div><Label>Start Date</Label><Input type="datetime-local" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                  <div><Label>End Date</Label><Input type="datetime-local" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>

                  <div>
                    <Label>Discount Type</Label>
                    <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Discount</SelectItem>
                        <SelectItem value="flat">Flat Discount</SelectItem>
                        <SelectItem value="buy_x_get_y">Buy X Get Y</SelectItem>
                        <SelectItem value="first_order">First Order Discount</SelectItem>
                        <SelectItem value="free_item">Free Item</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {(form.discount_type === 'percentage' || form.discount_type === 'first_order') && (
                    <>
                      <div><Label>Discount Percentage</Label><Input type="number" value={form.discount_percentage} onChange={(e) => setForm({ ...form, discount_percentage: e.target.value })} /></div>
                      <div><Label>Maximum Discount</Label><Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} /></div>
                    </>
                  )}
                  {form.discount_type === 'flat' && <div><Label>Flat Discount</Label><Input type="number" value={form.flat_discount} onChange={(e) => setForm({ ...form, flat_discount: e.target.value })} /></div>}
                  <div><Label>Minimum Cart Value</Label><Input type="number" value={form.min_cart_value} onChange={(e) => setForm({ ...form, min_cart_value: e.target.value })} /></div>

                  {form.discount_type === 'buy_x_get_y' && (
                    <>
                      <div><Label>Buy Product</Label><Select value={form.buy_x_get_y.buy_product_id} onValueChange={(v) => setForm({ ...form, buy_x_get_y: { ...form.buy_x_get_y, buy_product_id: v } })}><SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger><SelectContent>{menuItems.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label>Buy Quantity</Label><Input type="number" value={form.buy_x_get_y.buy_quantity} onChange={(e) => setForm({ ...form, buy_x_get_y: { ...form.buy_x_get_y, buy_quantity: e.target.value } })} /></div>
                      <div><Label>Get Product</Label><Select value={form.buy_x_get_y.get_product_id} onValueChange={(v) => setForm({ ...form, buy_x_get_y: { ...form.buy_x_get_y, get_product_id: v } })}><SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger><SelectContent>{menuItems.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent></Select></div>
                      <div><Label>Free Quantity</Label><Input type="number" value={form.buy_x_get_y.free_quantity} onChange={(e) => setForm({ ...form, buy_x_get_y: { ...form.buy_x_get_y, free_quantity: e.target.value } })} /></div>
                    </>
                  )}

                  <div>
                    <Label>Product Targeting</Label>
                    <Select value={form.product_targeting.apply_to} onValueChange={(v) => setForm({ ...form, product_targeting: { ...form.product_targeting, apply_to: v } })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_products">All Products</SelectItem>
                        <SelectItem value="specific_product">Specific Product</SelectItem>
                        <SelectItem value="specific_category">Specific Category</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.product_targeting.apply_to === 'specific_product' && <div><Label>Target Product</Label><Select value={form.product_targeting.product_id || ''} onValueChange={(v) => setForm({ ...form, product_targeting: { ...form.product_targeting, product_id: v } })}><SelectTrigger><SelectValue placeholder="Select product" /></SelectTrigger><SelectContent>{menuItems.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent></Select></div>}
                  {form.product_targeting.apply_to === 'specific_category' && <div><Label>Target Category</Label><Select value={form.product_targeting.category_id || ''} onValueChange={(v) => setForm({ ...form, product_targeting: { ...form.product_targeting, category_id: v } })}><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>}

                  <div>
                    <Label>User Targeting</Label>
                    <Select value={form.user_targeting.target_type} onValueChange={(v) => setForm({ ...form, user_targeting: { ...form.user_targeting, target_type: v } })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_users">All Users</SelectItem>
                        <SelectItem value="logged_in_users">Logged-in Users</SelectItem>
                        <SelectItem value="first_time_users">First Time Users</SelectItem>
                        <SelectItem value="specific_users">Specific Users</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.user_targeting.target_type === 'specific_users' && (
                    <div className="md:col-span-2"><Label>Email list (comma separated)</Label><Textarea value={form.user_targeting.emails} onChange={(e) => setForm({ ...form, user_targeting: { ...form.user_targeting, emails: e.target.value } })} /></div>
                  )}

                  <div><Label>Total Usage Limit</Label><Input type="number" value={form.usage_rules.total_usage_limit} onChange={(e) => setForm({ ...form, usage_rules: { ...form.usage_rules, total_usage_limit: e.target.value } })} /></div>
                  <div><Label>Per User Limit</Label><Input type="number" value={form.usage_rules.per_user_limit} onChange={(e) => setForm({ ...form, usage_rules: { ...form.usage_rules, per_user_limit: e.target.value } })} /></div>

                  <div>
                    <Label>Allow with other promotions?</Label>
                    <Select value={form.allow_stacking ? 'yes' : 'no'} onValueChange={(v) => setForm({ ...form, allow_stacking: v === 'yes' })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No (exclusive)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit">{editing ? 'Save Changes' : 'Create Promotion'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader><CardTitle>Promotions</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2">Promotion Name</th><th>Type</th><th>Discount</th><th>Start Date</th><th>End Date</th><th>Usage</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((p) => (
                  <tr key={p.id} className="border-b">
                    <td className="py-2">{p.name}</td>
                    <td>{p.discount_type}</td>
                    <td>{formatDiscount(p)}</td>
                    <td>{new Date(p.start_date).toLocaleString()}</td>
                    <td>{new Date(p.end_date).toLocaleString()}</td>
                    <td>{p.usage}</td>
                    <td>{p.status}</td>
                    <td className="space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => editPromotion(p)}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => disablePromotion(p.id)}>Disable</Button>
                      <Button size="sm" variant="destructive" onClick={() => deletePromotion(p.id)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="giftcards" className="space-y-4">
        <Card>
          <CardHeader><CardTitle>Create Gift Card</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={createGiftCard} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Gift Card Name</Label><Input required value={giftForm.name} onChange={(e) => setGiftForm({ ...giftForm, name: e.target.value })} /></div>
              <div><Label>Gift Card Code</Label><Input value={giftForm.code} onChange={(e) => setGiftForm({ ...giftForm, code: e.target.value.toUpperCase() })} /></div>
              <div><Label>Discount Type</Label><Select value={giftForm.type} onValueChange={(v) => setGiftForm({ ...giftForm, type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="FLAT">Flat</SelectItem><SelectItem value="PERCENTAGE">Percentage</SelectItem></SelectContent></Select></div>
              <div><Label>Value</Label><Input type="number" required value={giftForm.value} onChange={(e) => setGiftForm({ ...giftForm, value: e.target.value })} /></div>
              <div><Label>Expiry Date</Label><Input type="datetime-local" required value={giftForm.expiry_date} onChange={(e) => setGiftForm({ ...giftForm, expiry_date: e.target.value })} /></div>
              <div><Label>Usage Limit</Label><Input type="number" required value={giftForm.usage_limit} onChange={(e) => setGiftForm({ ...giftForm, usage_limit: e.target.value })} /></div>
              <Button type="submit" className="w-fit">Create Gift Card</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Gift Cards</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead><tr className="text-left border-b"><th className="py-2">Name</th><th>Code</th><th>Type</th><th>Value</th><th>Expiry</th><th>Usage</th><th>Redemption Link</th></tr></thead>
              <tbody>
                {giftCards.map((g) => (
                  <tr key={g.id} className="border-b">
                    <td className="py-2">{g.name}</td><td>{g.code}</td><td>{g.type}</td><td>{g.type === 'PERCENTAGE' ? `${g.value}%` : `₹${g.value}`}</td><td>{new Date(g.expiry_date).toLocaleString()}</td><td>{g.usage_count}/{g.usage_limit}</td><td className="font-mono text-xs">{g.redemption_link}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="usage">
        <Card>
          <CardHeader><CardTitle>Usage Logs</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead><tr className="text-left border-b"><th className="py-2">User</th><th>Promotion</th><th>Order ID</th><th>Discount Amount</th><th>Date</th></tr></thead>
              <tbody>
                {usageLogs.map((u) => (
                  <tr key={u.id} className="border-b">
                    <td className="py-2">{u.user || u.user_id}</td><td>{u.promotion || u.promotion_id}</td><td className="font-mono text-xs">{u.order_id}</td><td>₹{Number(u.discount_amount || 0).toFixed(2)}</td><td>{new Date(u.used_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="analytics" className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card><CardContent className="py-4">Promotion usage: <strong>{analytics.total_usage}</strong></CardContent></Card>
          <Card><CardContent className="py-4">Revenue generated: <strong>₹{Number(analytics.revenue_generated || 0).toFixed(2)}</strong></CardContent></Card>
          <Card><CardContent className="py-4">Total discount given: <strong>₹{Number(analytics.total_discount_given || 0).toFixed(2)}</strong></CardContent></Card>
        </div>
        <Card>
          <CardHeader><CardTitle>Top Performing Promotions</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(analytics.top_promotions || []).map((p) => (
                <div key={p.promotion_id} className="flex justify-between border rounded px-3 py-2">
                  <span>{p.name}</span><span>{p.usage} uses</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
